import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Download, Upload, Trash2, Plus, Zap, Copy, Check, AlertCircle, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getPricingSettings, updatePricingSettings } from "@/lib/api";
import api from "@/lib/api";

const DEFAULT_CLIENT_TYPES = ["Private", "Bairstow Eves", "Hamptons", "Gladstone", "Keatons", "Grant J Bates"];
const COMMISSION_OPTIONS = [
  { value: "none", label: "0% — No Commission", rate: 54 },
  { value: "12.5+vat", label: "12.5% + VAT (£64/hr)", rate: 64 },
  { value: "20+vat", label: "20% + VAT (£67/hr)", rate: 67 },
];

export default function Settings() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [emailWebhookUrl, setEmailWebhookUrl] = useState("");
  const [bankInfo, setBankInfo] = useState({ name: "", account: "", sort_code: "" });
  const [savingBank, setSavingBank] = useState(false);
  // Initial state must default to enabled:false. Earlier it defaulted to enabled:true,
  // so if the GET below failed silently and the user clicked "Save Chase Rules", the
  // PUT would re-enable chases. Real emails to real clients fire from this toggle —
  // safest default is OFF.
  const [chaseConfig, setChaseConfig] = useState({ enabled: false, rules: { _default: { chase1: 20, chase2: 30, terms: 30, paused: false } } });
  const [savingChase, setSavingChase] = useState(false);

  // Dynamic client types from pricing keys
  const CLIENT_TYPES = useMemo(() => {
    const keys = Object.keys(pricing);
    const all = new Set([...DEFAULT_CLIENT_TYPES, ...keys]);
    return Array.from(all);
  }, [pricing]);

  useEffect(() => {
    Promise.all([
      getPricingSettings(),
      api.get("/settings/zapier-webhook").then(r => r.data),
      api.get("/settings/zapier-email-webhook").then(r => r.data).catch(() => ({ url: "" })),
      api.get("/settings/bank-info").then(r => r.data).catch(() => ({ name: "", account: "", sort_code: "" })),
      api.get("/settings/chase-config").then(r => r.data).catch(() => null),
    ]).then(([p, w, ew, bi, cc]) => {
      setPricing(p);
      // Seed the "last saved" snapshot so blur events on untouched fields don't
      // trigger phantom saves (and so we can detect what the user *actually* edits).
      const snap = {};
      Object.entries(p || {}).forEach(([ct, cfg]) => {
        if (cfg && typeof cfg === "object") {
          Object.entries(cfg).forEach(([f, v]) => { snap[`${ct}|${f}`] = v; });
        }
      });
      lastSavedSnapshotRef.current = snap;
      setWebhookUrl(w.url || "");
      setEmailWebhookUrl(ew.url || "");
      setBankInfo({ name: bi.name || "", account: bi.account || "", sort_code: bi.sort_code || "" });
      if (cc) setChaseConfig(cc);
    }).catch(() => toast.error("Failed to load settings"))
    .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        updatePricingSettings(pricing),
        api.put("/settings/zapier-webhook", { url: webhookUrl }),
        api.put("/settings/zapier-email-webhook", { url: emailWebhookUrl }),
      ]);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBankInfo = async () => {
    const name = (bankInfo.name || "").trim();
    const account = (bankInfo.account || "").trim();
    const sort_code = (bankInfo.sort_code || "").trim();
    if (!name) { toast.error("Business name is required"); return; }
    if (!account) { toast.error("Account number is required"); return; }
    if (!sort_code) { toast.error("Sort code is required"); return; }
    setSavingBank(true);
    try {
      const r = await api.put("/settings/bank-info", { name, account, sort_code });
      setBankInfo({ name: r.data.name, account: r.data.account, sort_code: r.data.sort_code });
      toast.success("Bank details saved");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save bank details";
      toast.error(msg);
    } finally {
      setSavingBank(false);
    }
  };

  const handleSaveChaseConfig = async () => {
    // Light-touch validation: chase2 must be strictly after chase1
    for (const [ct, r] of Object.entries(chaseConfig.rules || {})) {
      const c1 = parseInt(r.chase1, 10);
      const c2 = parseInt(r.chase2, 10);
      if (Number.isFinite(c1) && Number.isFinite(c2) && c2 <= c1) {
        toast.error(`${ct === "_default" ? "Default" : ct}: Chase 2 day must be after Chase 1`);
        return;
      }
    }
    setSavingChase(true);
    try {
      const r = await api.put("/settings/chase-config", chaseConfig);
      setChaseConfig(r.data);
      toast.success("Chase rules saved");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save chase rules";
      toast.error(msg);
    } finally {
      setSavingChase(false);
    }
  };

  const updateChaseRule = (clientType, field, value) => {
    setChaseConfig(prev => ({
      ...prev,
      rules: {
        ...(prev.rules || {}),
        [clientType]: { ...(prev.rules?.[clientType] || { chase1: 20, chase2: 30, terms: 30, paused: false }), [field]: value },
      },
    }));
  };

  const updateRule = (clientType, field, value) => {
    setPricing(prev => ({
      ...prev,
      [clientType]: {
        ...prev[clientType],
        [field]: value,
      },
    }));
  };

  // ── Per-row save indicator state ──────────────────────────────────────────
  // Key = `${clientType}|${field}`. Value = "saving" | "saved" | "error".
  // Drives the green tick / spinner / red alert beside each input.
  const [rowStatus, setRowStatus] = useState({});
  const rowStatusKey = (ct, field) => `${ct}|${field}`;
  // Track the last-saved snapshot so we can no-op blur events that don't move
  // the value (otherwise tabbing through fires a save with the same value).
  const lastSavedSnapshotRef = useRef({});

  // Light-touch validation. £0 is allowed (some clients fully commission). 99999+
  // and negatives are bounced. NaN/empty string also rejected so the user gets a
  // visible error instead of silently saving the previous value.
  const validateRate = (field, value) => {
    if (field === "hourly_rate") {
      if (value === "" || value === null || value === undefined) return "Rate cannot be empty";
      const n = Number(value);
      if (Number.isNaN(n)) return "Rate must be a number";
      if (n < 0) return "Rate cannot be negative";
      if (n > 1000) return "Rate looks too high — please check";
    }
    return null;
  };

  // Persist a single rate field. Called on blur (Input.onBlur) and on Enter.
  // Optimistic update is already in `pricing` via updateRule; this just pushes
  // the full pricing dict to the backend with the explicit user attribution so
  // the audit log captures who changed what.
  const saveRateField = async (clientType, field, valueOverride) => {
    const value = valueOverride !== undefined ? valueOverride : pricing?.[clientType]?.[field];
    const errMsg = validateRate(field, value);
    const key = rowStatusKey(clientType, field);
    if (errMsg) {
      setRowStatus(s => ({ ...s, [key]: "error" }));
      toast.error(`${clientType} ${field}: ${errMsg}`);
      return false;
    }
    // No-op if value hasn't changed since the last successful save (avoids
    // chatter when the user tabs through a row).
    if (lastSavedSnapshotRef.current[key] === value) {
      return true;
    }
    setRowStatus(s => ({ ...s, [key]: "saving" }));
    try {
      const userName = localStorage.getItem("desktop_user_name") || "Office user";
      const fullRules = { ...pricing, [clientType]: { ...pricing[clientType], [field]: value } };
      await api.put(`/settings/pricing?user=${encodeURIComponent(userName)}`, fullRules);
      lastSavedSnapshotRef.current[key] = value;
      setRowStatus(s => ({ ...s, [key]: "saved" }));
      // Fade the tick after 2.5s so the row goes quiet once the user moves on.
      setTimeout(() => setRowStatus(s => {
        if (s[key] !== "saved") return s; // a later save/error already took over
        const next = { ...s }; delete next[key]; return next;
      }), 2500);
      return true;
    } catch (e) {
      console.error("Failed to save rate", e);
      setRowStatus(s => ({ ...s, [key]: "error" }));
      toast.error(`${clientType} ${field}: failed to save`, {
        action: {
          label: "Retry",
          onClick: () => saveRateField(clientType, field, value),
        },
      });
      return false;
    }
  };

  // Tiny inline status badge — renders next to each saveable rate field.
  const RowStatus = ({ clientType, field }) => {
    const s = rowStatus[rowStatusKey(clientType, field)];
    if (s === "saving") return <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--text-muted)" }} aria-label="Saving" data-testid={`rate-status-saving-${clientType}-${field}`} />;
    if (s === "saved") return <Check className="w-4 h-4" style={{ color: "#16A34A" }} aria-label="Saved" data-testid={`rate-status-saved-${clientType}-${field}`} />;
    if (s === "error") return <AlertCircle className="w-4 h-4" style={{ color: "#DC2626" }} aria-label="Failed to save" data-testid={`rate-status-error-${clientType}-${field}`} />;
    return <span className="w-4 h-4 inline-block" />; // reserve space so width is stable
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading...</p></div>;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className="px-8 pt-8 pb-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="ui-page-title">Settings</h1>
          <Button onClick={handleSave} disabled={saving} className="btn-primary gap-2 h-10 px-5" data-testid="save-settings">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save All Settings"}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl px-8 pb-8 space-y-6">
        {/* Theme Selector */}
        <section className="ui-surface p-6">
          <h2 className="ui-section-title mb-1">Appearance</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>Choose your preferred colour theme.</p>
          <div className="grid grid-cols-3 gap-4">
            {[
              { id: "warm", label: "Warm", desc: "Earthy terracotta tones", colors: ["#F9F8F6", "#E07A5F", "#1C1917"] },
              { id: "ocean", label: "Ocean", desc: "Cool blue professional", colors: ["#F0F4F8", "#2680C2", "#102A43"] },
              { id: "dark", label: "Dark", desc: "Easy on the eyes", colors: ["#1A1A2E", "#7C5CFC", "#F1F0F5"] },
            ].map(t => {
              const active = (localStorage.getItem("app_theme") || "warm") === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => {
                    localStorage.setItem("app_theme", t.id);
                    document.documentElement.setAttribute("data-theme", t.id);
                    window.dispatchEvent(new Event("theme-change"));
                  }}
                  className="rounded-xl text-left transition-all"
                  style={{ minHeight: "100px", padding: "16px", border: active ? "2px solid #E05C2A" : "1px solid #E5E7EB", background: active ? "#FFF8F5" : "var(--surface)" }}
                  data-testid={`theme-${t.id}`}
                >
                  <div className="flex gap-2 mb-3">
                    {t.colors.map((c, i) => <div key={i} style={{ width: "24px", height: "24px", borderRadius: "50%", background: c, border: "2px solid #fff", outline: "1px solid #D1D5DB" }} />)}
                  </div>
                  <p style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" }}>{t.label}</p>
                  <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>{t.desc}</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Labour Pricing */}
        <section className="ui-surface p-6">
          <h2 className="ui-section-title mb-1">Job Pricing — Labour</h2>
          <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>Labour rates are fixed company-wide and cannot be edited. 0% commission = £54/hr, 12.5% = £64/hr, 20% = £67/hr (named client rates may differ). 1 day = 8 hours. 1 hour = Call Out = £80 fixed. Add new client types from the New Job page.</p>
          {/* Column headers */}
          <div className="flex items-center gap-4 pb-2 mb-2" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="w-40"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Client</span></div>
            <div className="w-20 text-right"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Hourly Rate (£)</span></div>
            <div className="flex-1"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Commission</span></div>
            <div className="w-44 text-right"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Auto-apply on Mark Paid</span></div>
          </div>
          <div>
            {CLIENT_TYPES.map((ct, idx) => {
              const rule = pricing[ct] || {};
              const hasCommission = (rule.commission_type || "none") !== "none";
              const autoApply = rule.auto_apply_commission !== false && hasCommission;
              return (
                <div key={ct} className="flex items-center gap-4" style={{ padding: "14px 0", borderBottom: idx < CLIENT_TYPES.length - 1 ? "1px solid var(--border)" : "none", background: idx % 2 === 1 ? "#F9FAFB" : "transparent", margin: "0 -8px", paddingLeft: "8px", paddingRight: "8px", borderRadius: "4px" }}>
                  <div className="w-40">
                    <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{ct}</span>
                  </div>
                  <div className="w-20 flex items-center gap-1.5">
                    <div
                      className="w-20 h-9 rounded-md text-sm font-mono text-right flex items-center justify-end px-3"
                      style={{ border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text-secondary)", cursor: "not-allowed" }}
                      title="Labour rates are fixed company-wide and cannot be edited"
                      data-testid={`hourly-rate-${ct.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      £{Number(rule.hourly_rate ?? 54).toFixed(0)}
                    </div>
                    <Lock className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} aria-label="Locked" />
                  </div>
                  <div style={{ maxWidth: "240px" }}>
                    <select
                      value={rule.commission_type || "none"}
                      onChange={(e) => {
                        const comm = COMMISSION_OPTIONS.find(o => o.value === e.target.value);
                        const updates = { commission_type: e.target.value, materials_rule: e.target.value === "none" ? "private" : "commission" };
                        if (comm?.rate) updates.hourly_rate = comm.rate;
                        const newRule = { ...pricing[ct], ...updates };
                        setPricing(prev => ({ ...prev, [ct]: newRule }));
                        // Commission flip writes commission_type AND hourly_rate — save both so the
                        // audit log shows the bundled change as two entries (clearer history).
                        saveRateField(ct, "commission_type", updates.commission_type);
                        if (updates.hourly_rate !== undefined) saveRateField(ct, "hourly_rate", updates.hourly_rate);
                      }}
                      className="h-9 rounded-md text-sm px-2 w-full"
                      style={{ border: "1px solid var(--border)", maxWidth: "240px" }}
                      data-testid={`commission-${ct.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {COMMISSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="w-44 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        const newVal = !autoApply;
                        setPricing(prev => ({ ...prev, [ct]: { ...prev[ct], auto_apply_commission: newVal } }));
                        saveRateField(ct, "auto_apply_commission", newVal);
                      }}
                      disabled={!hasCommission}
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                      style={{ background: autoApply ? "#E05C2A" : "#CBD5E1", opacity: hasCommission ? 1 : 0.4, cursor: hasCommission ? "pointer" : "not-allowed" }}
                      data-testid={`auto-commission-${ct.replace(/\s+/g, "-").toLowerCase()}`}
                      role="switch"
                      aria-checked={autoApply}
                      title={hasCommission ? "When ON, Mark Paid opens with Commission pre-selected and the expected net pre-filled" : "Set a commission rate to enable"}
                    >
                      <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" style={{ transform: autoApply ? "translateX(22px)" : "translateX(2px)" }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Materials Rules */}
        <section className="ui-surface p-6">
          <h2 className="ui-section-title mb-1">Job Pricing — Materials</h2>
          <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
            User enters materials price inc VAT. Calculation depends on client type.
          </p>
          <div>
            {CLIENT_TYPES.map((ct, idx) => {
              const rule = pricing[ct] || {};
              const isCommission = rule.materials_rule === "commission";
              const formula = isCommission ? "Inc VAT ÷ 0.85 = ex VAT price (commission client)" : "Inc VAT ÷ 1.2 = ex VAT + optional markup (0-20%)";
              return (
                <div key={ct} className="flex items-center justify-between" style={{ padding: "14px 8px", borderBottom: idx < CLIENT_TYPES.length - 1 ? "1px solid var(--border)" : "none", background: idx % 2 === 1 ? "#F9FAFB" : "transparent", borderRadius: "4px", margin: "0 -8px" }}>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" }}>{ct}</span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold" style={isCommission ? { background: "#fff7ed", color: "#ea580c" } : { background: "#dcfce7", color: "#16a34a" }}>
                      {isCommission ? "Commission" : "Standard"}
                    </span>
                    <span className="cursor-help text-sm" title={formula} style={{ color: "var(--text-muted)" }}>ℹ</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Zapier Integration */}
        <section className="p-5" style={{ border: "1px solid var(--border)", borderRadius: "8px", background: "var(--surface)" }}>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-5 h-5" style={{ color: "#FF4A00" }} />
            <h2 className="ui-section-title">Integrations — Zapier</h2>
          </div>
          <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
            Automatically create calendar invites and send emails when jobs are booked. Connect your Zapier account and paste your webhook URLs below.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Booking Webhook URL</Label>
              <div className="flex gap-2">
                <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://hooks.zapier.com/..." className="flex-1 rounded-md" style={{ border: "1px solid var(--border)" }} />
                <button onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }} className="flex items-center justify-center w-9 h-9 rounded-md transition-colors hover:bg-slate-100" style={{ border: "1px solid var(--border)" }} title="Copy"><Copy className="w-4 h-4" style={{ color: "var(--text-muted)" }} /></button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Email Webhook URL (Quote/Invoice sending)</Label>
              <div className="flex gap-2">
                <Input value={emailWebhookUrl} onChange={(e) => setEmailWebhookUrl(e.target.value)} placeholder="https://hooks.zapier.com/..." className="flex-1 rounded-md" style={{ border: "1px solid var(--border)" }} />
                <button onClick={() => { navigator.clipboard.writeText(emailWebhookUrl); toast.success("Copied"); }} className="flex items-center justify-center w-9 h-9 rounded-md transition-colors hover:bg-slate-100" style={{ border: "1px solid var(--border)" }} title="Copy"><Copy className="w-4 h-4" style={{ color: "var(--text-muted)" }} /></button>
              </div>
            </div>
          </div>
        </section>

        {/* Bank Details (shown on quote and invoice PDFs) */}
        <section className="ui-surface p-6" data-testid="bank-details-section">
          <h2 className="ui-section-title mb-1">Bank Details</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>These details appear on every quote and invoice PDF, and on the second payment reminder email.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Business name</Label>
              <Input
                value={bankInfo.name}
                onChange={(e) => setBankInfo(p => ({ ...p, name: e.target.value }))}
                placeholder="Hammond Property Solutions"
                className="rounded-md"
                style={{ border: "1px solid var(--border)" }}
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Sort code</Label>
              <Input
                value={bankInfo.sort_code}
                onChange={(e) => setBankInfo(p => ({ ...p, sort_code: e.target.value }))}
                placeholder="82-19-74"
                className="rounded-md font-mono"
                style={{ border: "1px solid var(--border)" }}
                data-testid="bank-sort-code-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={{ color: "var(--text-muted)" }}>Account number</Label>
              <Input
                value={bankInfo.account}
                onChange={(e) => setBankInfo(p => ({ ...p, account: e.target.value }))}
                placeholder="20599610"
                className="rounded-md font-mono"
                style={{ border: "1px solid var(--border)" }}
                data-testid="bank-account-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Preview: <span className="font-semibold" style={{ color: "var(--text-secondary)" }}>{bankInfo.name || "—"}</span>
              <span style={{ color: "var(--text-muted)" }}> · Account: </span>
              <span className="font-mono" style={{ color: "var(--text-secondary)" }}>{bankInfo.account || "—"}</span>
              <span style={{ color: "var(--text-muted)" }}> · Sort Code: </span>
              <span className="font-mono" style={{ color: "var(--text-secondary)" }}>{bankInfo.sort_code || "—"}</span>
            </p>
            <Button onClick={handleSaveBankInfo} disabled={savingBank} className="gap-1" size="sm" style={{ background: "var(--primary)", color: "#fff" }} data-testid="save-bank-info">
              <Save className="w-3.5 h-3.5" /> {savingBank ? "Saving..." : "Save bank details"}
            </Button>
          </div>
        </section>

        {/* Email Chasing — automated invoice payment reminders */}
        <section className="ui-surface p-6" data-testid="chase-config-section">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <h2 className="ui-section-title mb-1">Email Chasing</h2>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Automatic payment reminder emails for unpaid invoices. Day offsets count from the invoice's original sent date.</p>
            </div>
            {/* Master on/off */}
            <label className="flex items-center gap-2 cursor-pointer flex-shrink-0 pt-1" data-testid="chase-master-toggle-label">
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: chaseConfig.enabled ? "#15803D" : "var(--text-muted)" }}>
                {chaseConfig.enabled ? "All chases ON" : "All chases OFF"}
              </span>
              <button
                onClick={() => setChaseConfig(p => ({ ...p, enabled: !p.enabled }))}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                style={{ background: chaseConfig.enabled ? "var(--primary)" : "#D1D5DB" }}
                data-testid="chase-master-toggle"
                aria-label="Toggle automated chase emails"
              >
                <span className="inline-block h-5 w-5 transform rounded-full bg-white transition" style={{ transform: chaseConfig.enabled ? "translateX(22px)" : "translateX(2px)" }} />
              </button>
            </label>
          </div>

          {/* Headers */}
          <div className="flex items-center gap-3 pt-4 pb-2 mb-2" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="flex-1"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Client Type</span></div>
            <div className="w-24 text-center"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Chase 1 (day)</span></div>
            <div className="w-24 text-center"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Chase 2 (day)</span></div>
            <div className="w-24 text-center"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Terms (day)</span></div>
            <div className="w-28 text-center"><span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Paused</span></div>
          </div>

          {/* Rows: every client type from pricing + the _default fallback at the bottom */}
          {[...CLIENT_TYPES, "_default"].map((ct, idx, arr) => {
            const isDefault = ct === "_default";
            const rule = (chaseConfig.rules || {})[ct] || (isDefault
              ? { chase1: 20, chase2: 30, terms: 30, paused: false }
              : { ...((chaseConfig.rules || {})._default || { chase1: 20, chase2: 30, terms: 30, paused: false }) });
            return (
              <div key={ct} className="flex items-center gap-3" style={{ padding: "12px 8px", borderBottom: idx < arr.length - 1 ? "1px solid var(--border)" : "none", background: idx % 2 === 1 ? "#F9FAFB" : "transparent", margin: "0 -8px", borderRadius: "4px", opacity: chaseConfig.enabled ? 1 : 0.55 }} data-testid={`chase-row-${ct}`}>
                <div className="flex-1">
                  <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{isDefault ? "All other clients" : ct}</span>
                  {isDefault && <span className="ml-2 text-[10px] uppercase font-semibold tracking-wide" style={{ color: "var(--text-muted)" }}>Fallback</span>}
                </div>
                <div className="w-24">
                  <Input type="number" min="0" value={rule.chase1 ?? ""} onChange={(e) => updateChaseRule(ct, "chase1", parseInt(e.target.value || "0", 10))} className="h-9 rounded-md text-sm text-center font-mono" style={{ border: "1px solid var(--border)" }} disabled={!chaseConfig.enabled} data-testid={`chase1-${ct}`} />
                </div>
                <div className="w-24">
                  <Input type="number" min="1" value={rule.chase2 ?? ""} onChange={(e) => updateChaseRule(ct, "chase2", parseInt(e.target.value || "0", 10))} className="h-9 rounded-md text-sm text-center font-mono" style={{ border: "1px solid var(--border)" }} disabled={!chaseConfig.enabled} data-testid={`chase2-${ct}`} />
                </div>
                <div className="w-24">
                  <Input type="number" min="0" value={rule.terms ?? ""} onChange={(e) => updateChaseRule(ct, "terms", parseInt(e.target.value || "0", 10))} className="h-9 rounded-md text-sm text-center font-mono" style={{ border: "1px solid var(--border)" }} disabled={!chaseConfig.enabled} data-testid={`terms-${ct}`} />
                </div>
                <div className="w-28 flex justify-center">
                  <button
                    onClick={() => updateChaseRule(ct, "paused", !rule.paused)}
                    className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                    style={{ background: rule.paused ? "#F59E0B" : "#D1D5DB" }}
                    disabled={!chaseConfig.enabled}
                    data-testid={`paused-${ct}`}
                    aria-label={`Pause chases for ${ct}`}
                    title={rule.paused ? "Chases paused for this client type" : "Chases active"}
                  >
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white transition" style={{ transform: rule.paused ? "translateX(18px)" : "translateX(2px)" }} />
                  </button>
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-between mt-4">
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {chaseConfig.enabled
                ? "Chases run once every 24h. Per-invoice pause toggles override these rules."
                : "All automated chase emails are currently disabled. Manual reminders still work."}
            </p>
            <Button onClick={handleSaveChaseConfig} disabled={savingChase} className="gap-1" size="sm" style={{ background: "var(--primary)", color: "#fff" }} data-testid="save-chase-config">
              <Save className="w-3.5 h-3.5" /> {savingChase ? "Saving..." : "Save chase rules"}
            </Button>
          </div>
        </section>

        {/* Bank Connection */}
        <section className="ui-surface p-6">
          <h2 className="ui-section-title mb-1">Bank Connections</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>Connect your bank accounts via Open Banking to auto-match payments and expenses.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg" style={{ border: "1px solid var(--border)" }}>
              <p className="text-sm font-semibold mb-1">Income (Virgin Money)</p>
              <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Match payments to invoices</p>
              <Button onClick={async () => { try { const r = await api.get("/bank/connect"); window.location.href = r.data.auth_url; } catch { toast.error("Failed"); } }} size="sm" className="gap-1" style={{ background: "var(--primary)", color: "#fff" }}>Connect Income Bank</Button>
            </div>
            <div className="p-4 rounded-lg" style={{ border: "1px solid var(--border)" }}>
              <p className="text-sm font-semibold mb-1">Expenses (Capital on Tap)</p>
              <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Auto-import expenses from card</p>
              <Button onClick={async () => { try { const r = await api.get("/bank/expenses-connect"); window.location.href = r.data.auth_url; } catch { toast.error("Failed"); } }} size="sm" className="gap-1" style={{ background: "var(--primary)", color: "#fff" }}>Connect Expenses Bank</Button>
            </div>
          </div>
        </section>

        {/* Data Management */}
        <section className="ui-surface p-6">
          <h2 className="ui-section-title mb-1">Data Management</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>Download a full backup or restore from a previous backup.</p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const pw = prompt("Enter backup password:");
                if (pw) window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/full-backup?password=${encodeURIComponent(pw)}`, "_blank");
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ background: "var(--primary-light)", color: "var(--primary)" }}
            >
              <Download className="w-4 h-4" /> Download Backup
            </button>
            <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              <Upload className="w-4 h-4" /> Restore from Backup
              <input type="file" accept=".json" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const pw = prompt("Enter backup password:");
                if (!pw) return;
                const fd = new FormData();
                fd.append("file", file);
                fd.append("password", pw);
                try {
                  await api.post("/import/full-restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success("Backup restored!");
                  window.location.reload();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Restore failed");
                } 
                e.target.value = "";
              }} />
            </label>
          </div>
          <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
            Backup includes: jobs, quotes, clients, team members, settings, expenses, pay records, to-dos, absences, media references.
          </p>
        </section>

        {/* Clear All Data */}
        <section className="ui-surface p-6" style={{ border: "2px solid var(--danger)" }}>
          <h2 className="ui-section-title mb-1" style={{ color: "var(--danger)" }}>Clear All Data</h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            Delete all jobs, quotes, clients, materials, expenses, notes, and todos. Resets quote counter to Q-0001. Team members and settings are kept.
          </p>
          <button
            onClick={async () => {
              const pw = prompt("Enter backup password to confirm:");
              if (!pw) return;
              if (!window.confirm("This will permanently DELETE all jobs, quotes, clients, materials, expenses, notes, and todos. This cannot be undone. Are you absolutely sure?")) return;
              try {
                const resp = await api.post(`/clear-all-data?password=${encodeURIComponent(pw)}`);
                const d = resp.data.details || {};
                toast.success(`All data cleared! ${Object.entries(d).map(([k,v]) => `${k}: ${v}`).join(", ")}`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed — check password");
              } 
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            style={{ background: "var(--danger)", color: "#fff" }}
            data-testid="clear-all-data-btn"
          >
            <Trash2 className="w-4 h-4" /> Clear All Data
          </button>
        </section>
      </div>
    </div>
  );
}
